// Guided-tour content for the admin / vendor panel.
//
// One entry per sidebar menu. `key` is the menu name used by getRouteMap (so the page path, and the /seller
// prefix for vendors, come from there). `points` are controls on that page: the tour finds each one by the
// text shown on screen (button label, tab, column heading, placeholder or tooltip) and highlights it. A point
// that is not on the page (for example hidden by permission) is skipped, so the same content works for every
// login type. Edit the wording here; nothing else needs to change.

import { TOP_GROUPS, MENU_ORDER, SUBGROUPS, DISPLAY_NAMES } from "../Sidebar";

const REPORT_POINTS = [
  { find: "Note", text: "Explains how this report is worked out, so you can trust the numbers." },
  { find: "Reset", text: "Clears every filter and goes back to the default period." },
  { find: "CSV", text: "Download the rows as a CSV file." },
  { find: "Excel", text: "Download an Excel file you can open and share." },
  { find: "Print / PDF", text: "Print the report or save it as a PDF." },
];

const report = (key, group, text, extra = []) => ({ key, group, title: key, text, points: [...extra, ...REPORT_POINTS] });

export const TOUR_CONTENT = [
  // ───────── Main
  { key: "Dashboard", group: "Main", title: "Dashboard", text: "Your day at a glance: sales, orders, stock problems and what needs attention.", points: [
    { find: "Refresh", text: "Reload the figures." },
    { find: "Needs your attention", text: "Things waiting on you: pending approvals, low stock and open tickets." },
    { find: "Recent orders", text: "The latest orders. Click one to open it." },
  ] },

  // ───────── Operations
  { key: "Orders", group: "Operations", title: "Orders", text: "Every customer order. Search, filter by status and open an order to see all of it.", points: [
    { find: "Refresh", text: "Reload the order list." },
    { find: "View order", text: "Opens the order: price breakdown, customer, delivery address, payment, shipping and status history." },
  ] },
  { key: "Online Orders", group: "Operations", title: "Online Orders", text: "Orders sent to a warehouse to be fulfilled, with the items and delivery estimate.", points: [
    { find: "table view", text: "Switch between the grid and the table view." },
    { find: "All warehouses", text: "Show only one warehouse's orders." },
  ] },
  { key: "Support Ticket", group: "Operations", title: "Support Tickets", text: "Questions and complaints from customers, with an owner and a priority.", points: [
    { find: "Refresh", text: "Reload the tickets." },
    { find: "Reset", text: "Clear the status, priority and owner filters." },
    { find: "Open conversation", text: "Opens the chat with the customer. Reply, mark in progress, change priority or reopen a closed ticket there." },
  ] },
  { key: "Warehouse", group: "Operations", title: "Warehouses", text: "The places stock is kept. Add a warehouse, view its details and switch it on or off.", points: [
    { find: "Create Warehouse", text: "Add a warehouse: basic information, contact details and its location on the map." },
    { find: "Clear", text: "Clear the search and status filters." },
  ] },

  // ───────── Products
  { key: "ProductManagement", group: "Products", title: "Product Management", text: "Your catalogue. Add and edit products, approve seller products and watch stock.", points: [
    { find: "Add product", text: "Create a product: basics, photos and video, details, pricing, stock and shipping, SEO." },
    { find: "Import", text: "Add many products at once from a file." },
    { find: "Export", text: "Download the product list." },
    { find: "More", text: "Extra filters: category, approval, live or not, stock level and who owns the product." },
    { find: "Needs approval", text: "Seller products waiting for you to approve or reject." },
    { find: "Low stock", text: "Products at or below their minimum stock." },
    { find: "Edit", text: "Change a product's details, photos, price and stock. Pick which photo is the main one." },
  ] },
  { key: "SellerProductRequest", group: "Products", title: "Seller Product Requests", text: "Products sellers ask to add to your catalogue, waiting for your decision.", points: [
    { find: "Waiting for a decision", text: "Requests nobody has answered yet." },
    { find: "View", text: "Compare the price the seller asks with your catalogue, then approve or reject." },
  ] },
  { key: "Categories", group: "Products", title: "Categories", text: "The category tree shoppers browse. Two tabs: categories and subcategories.", points: [
    { find: "Subcategories", text: "Switch to subcategories, which belong to a category." },
    { find: "Add category", text: "Create a category and place it in a cluster." },
  ] },
  { key: "Category Cluster", group: "Products", title: "Category Clusters", text: "Groups of categories used to organise the storefront and the app home screen.", points: [
    { find: "Add cluster", text: "Create a cluster, then put categories into it." },
    { find: "Empty clusters", text: "Clusters with no categories yet." },
  ] },
  { key: "Brands", group: "Products", title: "Brands", text: "The brands your products belong to.", points: [
    { find: "Add brand", text: "Create a brand with its logo and description." },
    { find: "Import from products", text: "Create brands automatically from the brand names already used on products." },
  ] },
  { key: "Variation Groups", group: "Products", title: "Variation Groups", text: "Link products that are the same item in different shades or sizes, so shoppers can switch between them.", points: [
    { find: "Create group", text: "Name the group, say what varies (shade, size) and pick the products to link." },
  ] },
  { key: "Expired", group: "Products", title: "Expired Stock", text: "Stock past its expiry date: waiting to be written off, or already written off.", points: [
    { find: "Waiting to be written off", text: "Expired batches still counted in the warehouse." },
    { find: "Total lost to expiry", text: "The cost value lost to expiry." },
    { find: "All warehouses", text: "Look at one warehouse only." },
  ] },

  // ───────── Promotions
  { key: "Coupons", group: "Promotions", title: "Coupons", text: "Discount codes customers can apply at checkout.", points: [
    { find: "Add Coupon", text: "Set the discount, usage limits, dates, who can use it and any card restrictions." },
    { find: "Expired", text: "Coupons past their end date." },
  ] },
  { key: "Combo Offers", group: "Promotions", title: "Combo Offers", text: "Bundles of products sold together at one price.", points: [
    { find: "Add combo", text: "A short step-by-step form: details, pictures, products, pricing, availability, review." },
    { find: "Live but out of stock", text: "Combos that are on sale but have a product with no stock." },
  ] },
  { key: "BOGO Offers", group: "Promotions", title: "BOGO Offers", text: "Buy-one-get-one style offers.", points: [
    { find: "Add BOGO offer", text: "Steps: details, pictures, what the customer buys, what they get, review." },
  ] },
  { key: "Rewards", group: "Promotions", title: "Rewards", text: "Loyalty points: customer balances, the earning rules and a history of changes.", points: [
    { find: "Rule versions", text: "How many points customers earn and what a point is worth. Each change is a new version." },
    { find: "Change history", text: "Who changed what, and when." },
  ] },

  // ───────── Campaign
  { key: "Email", group: "Campaign", title: "Email Campaigns", text: "Send email campaigns to groups of customers.", points: [
    { find: "New Campaign", text: "Write the message, pick the audience and send now or schedule it. A warning shows if the email may land in spam." },
    { find: "Track", text: "See who got it, who opened it and who failed." },
  ] },
  { key: "SMS", group: "Campaign", title: "SMS Campaigns", text: "Send approved template messages to your customers and track delivery.", points: [
    { find: "New Campaign", text: "Pick an approved template, choose the audience and send now or schedule it for later." },
    { find: "Campaigns", text: "Every campaign you have made. Use the status cards above to filter them." },
    { find: "Search name, campaign ID or template", text: "Find a campaign by its name, ID or the template it uses." },
    { find: "Status", text: "Show only drafts, scheduled, sending, sent or failed campaigns." },
    { find: "Duplicate", text: "Make a copy of a campaign with the same template and audience, ready to edit and send again." },
    { find: "Track", text: "Opens the campaign to see each customer's delivery status and any failures." },
    { find: "Templates", text: "The approved message templates available for campaigns, with a preview of each message and its variables." },
  ] },
  { key: "Whatsapp", group: "Campaign", title: "WhatsApp Campaigns", text: "Send approved WhatsApp templates to customers.", points: [
    { find: "New Campaign", text: "Pick a template, fill its values (fixed text or from the customer's record) and choose the audience." },
    { find: "Track", text: "Delivered and read counts for each campaign." },
  ] },
  { key: "Push Notification", group: "Campaign", title: "Push Notifications", text: "Notifications sent to customers' phones through the app.", points: [
    { find: "Scheduled", text: "Notifications waiting to go out. You can cancel them." },
    { find: "Failed", text: "Notifications that did not reach Firebase." },
  ] },

  // ───────── People
  { key: "Customer", group: "People", title: "Customers", text: "Shoppers and business customers.", points: [
    { find: "Add Customer", text: "Create a customer account by hand." },
    { find: "More filters", text: "Filter by status, customer or business, verified, gender and how they signed up." },
  ] },
  { key: "Users", group: "People", title: "Users", text: "Staff accounts and what they can open.", points: [
    { find: "Create User", text: "Add a staff member and give them a role." },
    { find: "Sign out everywhere", text: "Ends every signed-in session for that user." },
  ] },
  { key: "Roles", group: "People", title: "Roles", text: "A role decides which menus staff can see.", points: [
    { find: "Create role", text: "Name the role and tick the menus it can open." },
  ] },
  { key: "Sellers", group: "People", title: "Sellers", text: "Marketplace sellers: approval, commission and payout details.", points: [
    { find: "Add Seller", text: "Create a seller yourself." },
    { find: "Invite by email", text: "Send a seller a sign-up link." },
    { find: "More filters", text: "Filter by approval, active, location and more." },
  ] },
  { key: "Supplier", group: "People", title: "Suppliers", text: "Companies you buy stock from.", points: [
    { find: "More filters", text: "Filter by type, state and status." },
  ] },
  { key: "Seller Payout", group: "People", title: "Seller Payout", text: "What you owe sellers and what has been paid.", points: [
    { find: "Owed to sellers", text: "Total commission-adjusted amount still to be paid." },
    { find: "Pay", text: "Record a payment: bank transfer, UPI, cheque, cash or other." },
  ] },
  { key: "Supplier Payout", group: "People", title: "Supplier Payout", text: "Credit pending to suppliers and the payments made.", points: [
    { find: "Credit pending", text: "Invoices not yet paid." },
    { find: "Pay", text: "Record a payment against a supplier or one invoice. Full fills the whole balance." },
  ] },

  // ───────── Reports
  report("Sales & Revenue Report", "Reports", "Revenue and profit for a period."),
  report("Order Report", "Reports", "Orders by status for a period."),
  report("Invoice Report", "Reports", "Every invoice issued, with B2B / B2C, state and PDF links."),
  report("Payment Report", "Reports", "Payments received, including cash on delivery still to be collected."),
  report("Shipping Report", "Reports", "Shipments and the ones stuck on the way."),
  report("Return & Refund Report", "Reports", "Returns and refunds by route, with the customer's video and document."),
  report("Product Sales Report", "Reports", "Sales per product. Filter by category and brand, or find the slow sellers."),
  report("Category Report", "Reports", "How each category performs. Click a row for its subcategories."),
  report("Brand Report", "Reports", "How each brand performs, own products against seller products."),
  report("Inventory Report", "Reports", "Stock by warehouse, category and brand, and any mismatches."),
  report("Customer Ratings Report", "Reports", "Ratings customers have given, positive to negative."),
  report("Profit & Loss Report", "Reports", "Profit and loss statement for a period."),
  report("GST Summary Report", "Reports", "GST collected and payable, section by section."),
  report("Seller Sales & Commission Report", "Reports", "Seller sales, returns and the commission earned."),
  report("Seller Settlement Report", "Reports", "What each seller has been paid and what is still due."),
  report("Supplier Purchase Report", "Reports", "Purchases by supplier and warehouse."),
  report("Supplier Ledger Report", "Reports", "Owed against paid for each supplier."),
  report("Customer Report", "Reports", "Your customer base and how it grew."),
  report("Campaign Report", "Reports", "Results of your campaigns."),
  report("Marketing Report", "Reports", "A summary of marketing activity."),
  report("Support Report", "Reports", "Support tickets and the ones waiting for a reply."),
  report("Warranty Report", "Reports", "Warranty claims."),

  // ───────── Logs
  { key: "ActivityLog", group: "Logs", title: "Activity Log", text: "Who changed what, and when. Use it to trace a mistake.", points: [
    { find: "Activity today", text: "How many actions were recorded today." },
    { find: "All modules", text: "Limit the log to one area, such as Products or Orders." },
  ] },
  { key: "LoginLogs", group: "Logs", title: "Login Logs", text: "Sign-ins, failed attempts and sign-outs.", points: [
    { find: "Failed today", text: "Wrong-password attempts today. A sudden rise can mean someone is guessing." },
    { find: "All events", text: "Show only sign-ins, failures or sign-outs." },
  ] },

  // ───────── Settings
  { key: "MenuConfiguration", group: "Settings", title: "Menu Configuration", text: "Create the menus and submenus that roles can be given.", points: [
    { find: "Add menu", text: "Add a main menu." },
    { find: "Not given to any role", text: "Menus nobody can open yet." },
  ] },
  { key: "Shipping & Payment Config", group: "Settings", title: "Shipping & Payment Config", text: "Delivery and payment rules for customers (B2C) and business buyers (B2B).", points: [
    { find: "Copy B2C values to B2B", text: "Fills the business settings with the customer ones, so you only change the differences." },
    { find: "View details", text: "See exactly what a past change altered." },
  ] },
  { key: "MobileContent", group: "Settings", title: "Mobile Content", text: "What the app home screen shows: product rows, promotional banners and subcategory shortcuts.", points: [] },
  { key: "AppPolicy", group: "Settings", title: "App Policy", text: "Terms, privacy and other policies shown in the app and website.", points: [
    { find: "Add Policy", text: "Write a new policy, set when it takes effect and whether it is visible." },
  ] },
  { key: "FAQs", group: "Settings", title: "FAQs", text: "Help topics and the questions under them.", points: [
    { find: "Add Topic", text: "Create a topic, then add questions to it." },
  ] },

  // ───────── Account
  { key: "Profile", group: "Account", title: "Profile", text: "Your own details and where you are signed in.", points: [
    { find: "Edit profile", text: "Change your name, contact details and photo, or your password." },
    { find: "Recent sign-ins", text: "Your last successful sign-ins." },
    { find: "Sign out all other devices", text: "Ends your sessions everywhere except this one." },
  ] },
];

// What each top-level menu is for (shown when the tour reaches that menu)
const SECTION_TEXT = {
  Operations: "Day-to-day work: customer orders, support tickets and warehouses.",
  Products: "Everything about the catalogue: products, categories, brands, variations and expiry.",
  Promotions: "Ways to sell more: coupons, combo and BOGO offers and loyalty rewards.",
  Campaign: "Reach customers by email, SMS, WhatsApp and app notifications.",
  UserManagement: "Customers, staff, roles, sellers and suppliers, and what you owe them.",
  Reports: "Sales, stock, finance, customer and marketing reports you can export.",
  Logs: "A record of who did what and who signed in.",
  Settings: "Menus, shipping and payment rules, app content, policies and FAQs.",
  "HR Management": "Staff HR tools.",
  Account: "Your own profile and sign-ins.",
};

const lookup = Object.fromEntries(TOUR_CONTENT.map((e) => [e.key, e]));

const norm = (p) => (p && p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p);

// Steps for the parts of the screen that are on every page. They point at data-tour markers (set in the top
// bar and sidebar); a step whose marker is missing for this login is skipped.
const WELCOME_STEPS = [
  { kind: "ui", target: '[data-tour="sidebar"]', title: "Sidebar", text: "Your menus, grouped by job. Click a menu to open its page. Menus with a small arrow have more pages inside." },
  { kind: "ui", target: '[data-tour="help-button"]', title: "Help and tours", text: "Open this any time to start the full tour, a tour of one page, or to continue where you left off." },
  { kind: "ui", target: '[data-tour="notifications"]', title: "Notifications", text: "New orders, tickets and alerts arrive here. The red number is how many you have not read." },
  { kind: "ui", target: '[data-tour="profile-menu"]', title: "Your profile", text: "Open your profile, take the tour again, or log out." },
];

const INTRO = {
  admin: "This tour follows your sidebar from top to bottom. It opens each page you can use and explains what its buttons, filters and tabs do. Use Next and Previous, or press Esc to leave at any time.",
  vendor: "As a seller you manage your own products, orders and payouts here. This tour opens each page you can use and explains what its buttons, filters and tabs do. Use Next and Previous, or press Esc to leave at any time.",
};

// Builds every tour for this login. The full tour walks the sidebar in order; each page also has its own short
// tour (for the directory and the "Help for this page" button). `menus` is the menu tree this login received,
// so only what the user can open is included.
export function buildTours(routeMap, menus, portal = "admin") {
  // same mapping and ordering rules as the sidebar
  const mapMenu = (items) => (Array.isArray(items) ? items : []).map((item) => {
    let title = item.menuName || item.submenuName || item.name;
    if (title === "Queries") title = "Support Ticket";
    const route = routeMap[title];
    const submenus = mapMenu(item.submenus);
    if (!route && submenus.length === 0) return null;
    return { title, to: route || null, submenus };
  }).filter(Boolean);
  const rank = (name) => { const i = MENU_ORDER.indexOf(name); return i === -1 ? MENU_ORDER.length : i; };
  const sortMenu = (items) => items.map((it, idx) => ({ it, idx })).sort((x, y) => rank(x.it.title) - rank(y.it.title) || x.idx - y.idx).map(({ it }) => ({ ...it, submenus: sortMenu(it.submenus) }));

  const tree = sortMenu(mapMenu(menus));
  const used = new Set();
  const sections = TOP_GROUPS.map((g) => {
    const items = tree.filter((m) => g.names.includes(m.title));
    items.forEach((m) => used.add(m));
    return { label: g.label, items };
  }).filter((g) => g.items.length);
  const rest = tree.filter((m) => !used.has(m));
  if (rest.length) sections.push({ label: "More", items: rest });

  // children in the order the sidebar shows them (Reports is shown in themed groups)
  const orderedChildren = (parent) => {
    const defs = SUBGROUPS[parent.title];
    if (!defs) return parent.submenus;
    const seen = new Set();
    const out = [];
    defs.forEach((d) => d.items.forEach((n) => { const ch = parent.submenus.find((x) => x.title === n); if (ch && !seen.has(ch)) { seen.add(ch); out.push(ch); } }));
    parent.submenus.forEach((ch) => { if (!seen.has(ch)) out.push(ch); });
    return out;
  };

  const steps = [];
  const pageTours = [];
  const seenPaths = new Set();

  const addPage = (node, groupLabel) => {
    if (!node.to) return;
    const entry = lookup[node.title];
    const path = norm(node.to);
    const name = (entry && entry.title) || DISPLAY_NAMES[node.title] || node.title;
    const text = entry ? entry.text : `The ${name} page.`;
    const tourId = `page:${path}`;
    const own = [{ kind: "page", tourId, path, group: groupLabel, title: name, text }];
    (entry ? entry.points : []).forEach((pt) => own.push({ kind: "point", tourId, path, group: groupLabel, title: name, find: pt.find, target: pt.target, text: pt.text }));
    steps.push(...own);
    if (!seenPaths.has(path)) {
      seenPaths.add(path);
      pageTours.push({ id: tourId, title: name, description: text, group: groupLabel, route: path, steps: own });
    }
  };

  sections.forEach((sec) => {
    sec.items.forEach((menu) => {
      const kids = orderedChildren(menu);
      const menuName = DISPLAY_NAMES[menu.title] || menu.title;
      if (kids.length) {
        const names = kids.filter((k) => k.to).map((k) => DISPLAY_NAMES[k.title] || k.title.replace(/ Report$/, ""));
        steps.push({ kind: "section", group: sec.label, title: menuName, text: `${SECTION_TEXT[menu.title] || ""} ${names.length ? "It contains: " + names.join(", ") + "." : ""}`.trim() });
        kids.forEach((k) => addPage(k, `${sec.label} › ${menuName}`));
      } else {
        addPage(menu, sec.label);
      }
    });
  });

  // numbering: "page n of N" counts only real pages
  const total = steps.filter((s) => s.kind === "page").length;
  let n = 0;
  steps.forEach((s) => { if (s.kind === "page") n += 1; s.page = n; s.pages = total; });

  const roleName = portal === "vendor" ? "Vendor" : "Admin / Staff";
  const welcomeSteps = WELCOME_STEPS.map((w) => ({ ...w, tourId: "welcome", group: "Getting started" }));
  const full = {
    id: "full",
    title: `${roleName} guided tour`,
    description: `All ${total} pages you can use, in sidebar order.`,
    group: "Start here",
    steps: [{ kind: "intro", title: `${roleName} tour`, text: `${INTRO[portal === "vendor" ? "vendor" : "admin"]} (${total} pages)` }, ...welcomeSteps, ...steps],
  };
  const welcome = {
    id: "welcome",
    title: "Getting around",
    description: "The sidebar, notifications, your profile and where to find help.",
    group: "Start here",
    steps: welcomeSteps,
  };
  return { roleName, tours: [full, welcome, ...pageTours] };
}

// kept for callers that only want the full list of steps
export function buildSteps(routeMap, menus) {
  const { tours } = buildTours(routeMap, menus);
  return [...tours[0].steps, { kind: "end", title: "That is the tour", text: "You can run it again any time from the Help button at the top of the page." }];
}
